import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Editor from './components/Editor';
import Settings from './components/Settings';
import BlogHomepage from './components/BlogHomepage';
import PostPage from './components/PostPage';
import StaticFile from './components/StaticFile';
import About from './components/About';
import Privacy from './components/Privacy';
import './App.css';

interface User {
  id: string;
  email: string;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // Get API URL from environment variables
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  // Debug: Log the API URL being used
  console.log('API Base URL:', apiBaseUrl);

  useEffect(() => {
    // Check for existing authentication on app load
    const savedToken = localStorage.getItem('contentflow_token');
    const savedUser = localStorage.getItem('contentflow_user');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  // Check if API URL is defined
  if (!apiBaseUrl) {
    console.error('REACT_APP_API_URL environment variable is not defined!');
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Configuration Error</h1>
        <p>API URL is not configured. Please check your environment variables.</p>
      </div>
    );
  }

  const handleLogin = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    setIsAuthenticated(true);

    // Save to localStorage
    localStorage.setItem('contentflow_token', newToken);
    localStorage.setItem('contentflow_user', JSON.stringify(newUser));
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);

    // Clear localStorage
    localStorage.removeItem('contentflow_token');
    localStorage.removeItem('contentflow_user');
  };

  return (
    <Router>
      <Routes>
        {/* Public blog routes */}
        <Route path="/" element={<BlogHomepage apiBaseUrl={apiBaseUrl!} />} />
        <Route path="/post/:slug" element={<PostPage apiBaseUrl={apiBaseUrl!} />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* Static file routes */}
        <Route path="/ads.txt" element={<StaticFile filename="ads.txt" apiBaseUrl={apiBaseUrl!} />} />

        {/* CMS routes */}
        <Route
          path="/cms"
          element={
            isAuthenticated ? (
              <Dashboard
                token={token!}
                user={user!}
                onLogout={handleLogout}
                apiBaseUrl={apiBaseUrl!}
              />
            ) : (
              <Login onLogin={handleLogin} apiBaseUrl={apiBaseUrl!} />
            )
          }
        />

        <Route
          path="/cms/edit/:id"
          element={
            isAuthenticated ? (
              <Editor
                token={token!}
                user={user!}
                onLogout={handleLogout}
                apiBaseUrl={apiBaseUrl}
              />
            ) : (
              <Login onLogin={handleLogin} apiBaseUrl={apiBaseUrl!} />
            )
          }
        />

        <Route
          path="/cms/settings"
          element={
            isAuthenticated ? (
              <Settings
                token={token!}
                user={user!}
                onLogout={handleLogout}
                apiBaseUrl={apiBaseUrl}
              />
            ) : (
              <Login onLogin={handleLogin} apiBaseUrl={apiBaseUrl!} />
            )
          }
        />

        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
